/** What is working and what is not, over a window. Every number is counted from the rows. */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { request } from '../api/client';
import type { Health as HealthShape } from '../api/types';
import { duration, moment, num, percent } from '../components/format';
import {
  Failed,
  Loading,
  Meter,
  Metric,
  PageHead,
  Panel,
  Pill,
  TurnsChart,
} from '../components/ui';

export function Health() {
  const [days, setDays] = useState(14);
  const { data, isPending, error } = useQuery({
    queryKey: ['health', days, null],
    queryFn: () => request<HealthShape>(`/api/v1/admin/health?days=${days}`),
  });

  return (
    <>
      <PageHead
        eyebrow="Health"
        title="What is working, and what is not"
        note="Turns, answer times, tools and server errors, counted from the rows rather than from a feeling."
        actions={
          <select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            aria-label="Window"
          >
            <option value={1}>Today</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        }
      />
      {isPending ? <Loading what="Counting" /> : null}
      {error ? <Failed error={error} fallback="Could not read health." /> : null}
      {data ? <Body health={data} /> : null}
    </>
  );
}

function Body({ health }: { health: HealthShape }) {
  const approvals = Object.entries(health.approvals).sort(([, a], [, b]) => b - a);
  return (
    <>
      <div className="cards">
        <Metric
          label="Turns"
          value={num(health.turns_total)}
          note={`${num(health.turns_chat)} text · ${num(health.turns_voice)} voice · ${num(health.turns_night_shift)} night shift`}
        />
        <Metric
          label="Failed"
          value={percent(health.turns_failed, health.turns_total)}
          note={`${num(health.turns_failed)} turns did not complete`}
          {...(health.turns_failed ? { tone: 'bad' as const } : {})}
        />
        <Metric
          label="Text answer time"
          value={duration(health.latency_chat.median_ms)}
          note={`p95 ${duration(health.latency_chat.p95_ms)} over ${num(health.latency_chat.samples)} turns`}
        />
        <Metric
          label="Voice answer time"
          value={duration(health.latency_voice.median_ms)}
          note={`p95 ${duration(health.latency_voice.p95_ms)} over ${num(health.latency_voice.samples)} turns`}
        />
        <Metric
          label="Voice heard"
          value={`${health.voice_minutes_heard} min`}
          note={`${num(health.voice_characters_spoken)} characters spoken back`}
        />
      </div>

      <Panel eyebrow="Pulse" title="Turns a day">
        <TurnsChart
          days={health.days.map((d) => ({
            day: d.day,
            chat: d.chat_turns,
            voice: d.voice_turns,
            night: d.night_shift_runs,
            failed: d.failed_turns,
          }))}
        />
      </Panel>

      <div className="panel-grid" style={{ marginBottom: 22 }}>
        <Panel eyebrow="Not working" title="Server errors, grouped" flush>
          {health.failures.length === 0 ? (
            <div className="panel-body muted">No server error in the window.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Error</th>
                  <th className="numeric">Count</th>
                  <th className="numeric">Customers</th>
                  <th>Last</th>
                </tr>
              </thead>
              <tbody>
                {health.failures.map((group) => (
                  <tr key={group.fingerprint}>
                    <td className="wrap">
                      <span className="bad">{group.exception_type}</span>{' '}
                      <span className="muted">{group.status_code}</span>
                      <div className="mono muted small">{group.path}</div>
                      <div className="small">{group.summary}</div>
                    </td>
                    <td className="numeric">{num(group.count)}</td>
                    <td className="numeric muted">{num(group.organisations)}</td>
                    <td className="muted">{moment(group.last_seen_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel eyebrow="Tools" title="What Electra did for people" flush>
          {health.tools.length === 0 ? (
            <div className="panel-body muted">No tool was called in the window.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Tool</th>
                  <th className="numeric">Calls</th>
                  <th style={{ width: 120 }}>Failed</th>
                  <th className="numeric">Waiting</th>
                  <th>Last failure</th>
                </tr>
              </thead>
              <tbody>
                {health.tools.map((tool) => (
                  <tr key={tool.tool_name}>
                    <td>
                      {tool.tool_name}
                      <div className="muted small">last {moment(tool.last_called_at)}</div>
                    </td>
                    <td className="numeric">{num(tool.calls)}</td>
                    <td>
                      <Meter part={tool.failed} whole={tool.calls} bad />
                      <div className={`small ${tool.failed ? 'bad' : 'muted'}`}>
                        {tool.failed || '—'}
                      </div>
                    </td>
                    <td className={`numeric ${tool.awaiting_approval ? 'warn' : 'muted'}`}>
                      {tool.awaiting_approval || '—'}
                    </td>
                    <td className="muted wrap small">{tool.latest_failure ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <div className="panel-grid">
        <Panel eyebrow="Approvals" title="Things Electra asked before doing">
          {approvals.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              Nothing needed approval in the window.
            </p>
          ) : (
            <div className="stack">
              {approvals.map(([status, count]) => (
                <div key={status} className="row" style={{ justifyContent: 'space-between' }}>
                  <Pill value={status} />
                  <span className="numeric">{num(count)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel eyebrow="Models" title="Load by model" flush>
          {health.models.length === 0 ? (
            <div className="panel-body muted">No model call in the window.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Kind</th>
                  <th className="numeric">Calls</th>
                  <th className="numeric">In</th>
                  <th className="numeric">Cached</th>
                  <th className="numeric">Out</th>
                </tr>
              </thead>
              <tbody>
                {health.models.map((load) => (
                  <tr key={`${load.provider}/${load.model}/${load.kind}`}>
                    <td>
                      {load.model}
                      <div className="muted small">{load.provider}</div>
                    </td>
                    <td className="muted">{load.kind}</td>
                    <td className="numeric">{num(load.calls)}</td>
                    <td className="numeric muted">{num(load.input_tokens)}</td>
                    <td className="numeric muted">{num(load.cached_input_tokens)}</td>
                    <td className="numeric muted">{num(load.output_tokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </>
  );
}
